import {
  Tooltip as AriaTooltip,
  TooltipAnchor,
  useTooltipState,
} from 'ariakit/tooltip';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useId } from 'react';
import styled, { css } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import { getTooltipText } from '../../utils/get-tooltip-text';

type Placement = 'top' | 'bottom' | 'left' | 'right';

export interface ITooltipProps {
  tooltipText?: ReactNode;
  children: ReactNode;
  hideTooltip?: boolean;
  tooltipAnchorStyle?: CSSProperties;
  wordBreak?: CSSProperties['wordBreak'];
  id?: string;
  tooltipPlacement?: Placement;
  tabIndex?: number;
  tooltipAnchorRef?: RefObject<HTMLDivElement | null>;
  noAnchorRole?: boolean;
  className?: string;
}

export const Tooltip = ({
  tooltipText,
  tooltipPlacement,
  children,
  hideTooltip = false,
  tooltipAnchorStyle,
  wordBreak,
  id,
  tabIndex = 0,
  tooltipAnchorRef,
  noAnchorRole = false,
  className,
}: ITooltipProps) => {
  const defaultId = useId();
  const tooltipId = getTooltipText(id ?? defaultId);
  const tooltipState = useTooltipState({
    placement: tooltipPlacement ?? 'top',
    gutter: 8,
    timeout: 300,
  });

  if (!tooltipText || hideTooltip)
    return (
      <div ref={tooltipAnchorRef} style={tooltipAnchorStyle}>
        {children}
      </div>
    );

  return (
    <>
      <TooltipAnchor
        ref={tooltipAnchorRef}
        style={tooltipAnchorStyle}
        state={tooltipState}
        tabIndex={tabIndex}
        role={noAnchorRole ? undefined : 'button'}
        aria-label={id ? `Help information for ${id}` : undefined}
        aria-describedby={tooltipId}
        aria-labelledby={undefined}
        className={className}
      >
        {children}
      </TooltipAnchor>
      <StyledTooltip
        data-testid="tooltip"
        state={tooltipState}
        $wordBreak={wordBreak}
        tabIndex={-1}
        id={tooltipId}
      >
        {tooltipText}
      </StyledTooltip>
    </>
  );
};

export const getTooltipStyling = ({
  $wordBreak,
  theme,
}: {
  theme: ITheme;
  $wordBreak?: CSSProperties['wordBreak'];
}) => {
  return css`
    max-width: 292px;
    background-color: ${theme.palette.neutral[100]};
    word-break: ${$wordBreak ?? 'normal'};
    color: ${theme.palette.neutral['00']};
    padding: ${theme.spacing(1, 1.5)};
    border-radius: 3px;
    display: block;
    font-weight: 400;
    font-size: 0.875rem;
    z-index: 100000;
    overflow: hidden;
    white-space: pre-line;
  `;
};

const StyledTooltip = styled(AriaTooltip)<{
  theme: ITheme;
  $wordBreak?: CSSProperties['wordBreak'];
}>`
  ${getTooltipStyling}
`;
